import React, { useEffect, useState, useCallback } from 'react';
import HeroSection from '../../sections/HeroSection/HeroSection';
import styles from './GalleryPage.module.css';

// ─── Config ───────────────────────────────────────────────────────────────────
// Point directly to your local Nginx images folder
const BASE_URL = '/images';
const FOLDER = 'Website Images';
const MANIFEST_URL = `${BASE_URL}/${FOLDER}/manifest.json`;

// ── Lightbox ──────────────────────────────────────────────────────────────────
const Lightbox = ({ images, index, onClose, onPrev, onNext }) => {
  const img = images[index];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape')     onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft')  onPrev();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, onPrev, onNext]);

  return (
    <div className={styles.lightboxOverlay} onClick={onClose}>
      <button className={styles.lightboxClose} onClick={onClose} aria-label="Close">✕</button>

      <button
        className={`${styles.lightboxArrow} ${styles.lightboxArrowLeft}`}
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        aria-label="Previous"
      >‹</button>

      <div className={styles.lightboxImgWrap} onClick={(e) => e.stopPropagation()}>
        <img src={img} alt={`Gallery ${index + 1}`} className={styles.lightboxImg} />
        <p className={styles.lightboxCounter}>{index + 1} / {images.length}</p>
      </div>

      <button
        className={`${styles.lightboxArrow} ${styles.lightboxArrowRight}`}
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        aria-label="Next"
      >›</button>
    </div>
  );
};

// ── Uniform Grid ────────────────────────────────────────────────────────────
const MasonryGrid = ({ urls, onImageClick }) => {
  return (
    <div className={styles.grid}>
      {urls.map((url, i) => (
        <div
          key={url}
          className={styles.gridItem}
          style={{ '--delay': `${i * 40}ms` }}
          onClick={() => onImageClick(i)}
        >
          <div className={styles.gridImgWrap}>
            <img
              src={url}
              alt=""
              className={styles.gridImg}
              loading="lazy"
              decoding="async"
            />
            <div className={styles.gridOverlay}>
              <span className={styles.zoomIcon}>⊕</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Skeleton loader ───────────────────────────────────────────────────────────
const SkeletonGrid = () => (
  <div className={styles.grid}>
    {Array.from({ length: 9 }).map((_, i) => (
      <div key={i} className={styles.gridItem}>
        <div className={styles.gridImgWrap}>
          <div className={styles.skeleton} />
        </div>
      </div>
    ))}
  </div>
);

// ── Page ──────────────────────────────────────────────────────────────────────
const GalleryPage = () => {
  const [imageUrls,   setImageUrls]   = useState([]);
  const [status,      setStatus]      = useState('loading');
  const [errorMsg,    setErrorMsg]    = useState('');
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const fetchManifest = useCallback(async () => {
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch(`${MANIFEST_URL}?t=${Date.now()}`); // bust cache
      if (!res.ok) throw new Error(
        res.status === 404
          ? `manifest.json not found in ${FOLDER}/. See setup instructions.`
          : `HTTP ${res.status}`
      );
      const data = await res.json();
      if (!Array.isArray(data.images) || !data.images.length) {
        throw new Error('manifest.json is empty. Add image filenames to it.');
      }
      const urls = data.images.map(
        filename => `${BASE_URL}/${FOLDER}/${filename}`
      );
      setImageUrls(urls);
      setStatus('done');
    } catch (err) {
      setErrorMsg(err.message);
      setStatus('error');
    }
  }, []);

  useEffect(() => { fetchManifest(); }, [fetchManifest]);

  const openLightbox  = (i) => setLightboxIdx(i);
  const closeLightbox = ()  => setLightboxIdx(null);
  const prevImage     = ()  => setLightboxIdx(i => (i - 1 + imageUrls.length) % imageUrls.length);
  const nextImage     = ()  => setLightboxIdx(i => (i + 1) % imageUrls.length);

  return (
    <>
      <HeroSection
        title="Life at Berli"
        image="/images/30-DSC09441.jpg"
        imageAlt="Berli Brewing Co."
      />

      <section className={styles.gallerySection}>
        <div className={styles.galleryContainer}>

          <div className={styles.galleryHeader}>
            <span className={styles.eyebrow}>Moments &amp; Memories</span>
            <h2 className={styles.heading}>Inside Berli</h2>
            <p className={styles.subheading}>
              A glimpse into the warmth, craft, and character that define us.
            </p>
          </div>

          {status === 'loading' && <SkeletonGrid />}

          {status === 'error' && (
            <div className={styles.errorState}>
              <p className={styles.errorText}>{errorMsg}</p>
              <button className={styles.retryBtn} onClick={fetchManifest}>Try Again</button>
            </div>
          )}

          {status === 'done' && (
            <>
              <MasonryGrid urls={imageUrls} onImageClick={openLightbox} />
              <p className={styles.imageCount}>{imageUrls.length} photographs</p>
            </>
          )}

        </div>
      </section>

      {lightboxIdx !== null && (
        <Lightbox
          images={imageUrls}
          index={lightboxIdx}
          onClose={closeLightbox}
          onPrev={prevImage}
          onNext={nextImage}
        />
      )}
    </>
  );
};

export default GalleryPage;